/**
 * /api/agent + /api/routing + /api/session — thin typed wrappers.
 *
 * The gateway returns the canonical wire shape from @chaos/types; no
 * client-side massaging needed.
 */
import type {
  AgentDispatchAck,
  AgentDispatchRequest,
  CardPresetState,
  ModelPreset,
  SessionAction,
  SessionLifecycleResponse,
} from '@chaos/types';

import { apiJson } from './client';

export const agentApi = {
  // ── Routing ───────────────────────────────────────────────────────
  listPresets(): Promise<ModelPreset[]> {
    return apiJson<{ presets: ModelPreset[] }>('/routing/presets').then((r) => r.presets);
  },
  getCardPreset(cardId: string): Promise<CardPresetState> {
    return apiJson<CardPresetState>(`/routing/preset/${cardId}`);
  },
  setCardPreset(cardId: string, presetId: string | null): Promise<CardPresetState> {
    return apiJson<CardPresetState>(`/routing/preset/${cardId}`, {
      method: 'PUT',
      json: { presetId },
    });
  },

  // ── Agent dispatch ────────────────────────────────────────────────
  dispatch(input: AgentDispatchRequest): Promise<AgentDispatchAck> {
    return apiJson<AgentDispatchAck>('/agent/dispatch', { method: 'POST', json: input });
  },

  // ── Session lifecycle ─────────────────────────────────────────────
  getSessionByCard(cardId: string): Promise<{
    session: { id: string; cardId: string; state: string } | null;
  }> {
    return apiJson<{ session: { id: string; cardId: string; state: string } | null }>(
      `/session/by-card/${cardId}`,
    );
  },
  performAction(sessionId: string, action: SessionAction): Promise<SessionLifecycleResponse> {
    return apiJson<SessionLifecycleResponse>(`/session/${sessionId}/${action}`, {
      method: 'POST',
      json: {},
    });
  },
};

export const agentKeys = {
  all: ['agent'] as const,
  presets: () => [...agentKeys.all, 'presets'] as const,
  cardPreset: (cardId: string) => [...agentKeys.all, 'preset', cardId] as const,
  session: (cardId: string) => [...agentKeys.all, 'session', cardId] as const,
};
